import moment from 'moment';
import { EVALUATION_STATUSES, PROJECT_ASSESSMENT_STATUSES, MANAGER_STATUS } from '@/constants';
import {
  ProjectDTO,
  ProjectRequestDTO,
  ProjectAssessmentDTO,
  ProjectAssessmentPutDTO,
  ProjectAssessmentAverageDropsDTO,
  UserEngagementDTO,
  MemberPeerEvaluationDTO,
  HRMProjectDTO,
  HRMUserEngagementTimeSheetDTO,
  ProjectManager
} from '@/models';
import {
  fetchProjectById,
  fetchAverageSatAndComplexity,
  putProjectAssessment,
  updateProject,
  saveUserEngagements,
  saveAdjustReceivedDrops,
  fetchHRMProjects,
  fetchEditedHRMTimeSheets as fetchEditedHRMTimeSheetsApi,
  fetchProjectManagers,
  updateManagerStatus
} from '@/services';
import { remapHrmTimeSheets } from '@/helpers/hrm';

interface ProjectState {
  project: ProjectDTO;
  projectAssessmentId: number;
  projectFetching: boolean;
  isProjectEditing: boolean;
  averageSatAndComplexity: ProjectAssessmentAverageDropsDTO[];
  filter: boolean[];
  filterValues: any;
  hrmProjects: HRMProjectDTO[];
  hrmEditedTimeSheets: HRMUserEngagementTimeSheetDTO[];
  managers: ProjectManager[];
}

const initialState: ProjectState = {
  project: {} as ProjectDTO,
  projectAssessmentId: -1,
  projectFetching: false,
  isProjectEditing: false,
  averageSatAndComplexity: [],
  filter: [],
  filterValues: {},
  hrmProjects: [],
  hrmEditedTimeSheets: [],
  managers: []
};

const getters = {
  project: (state: ProjectState): ProjectDTO => {
    return state.project;
  },

  projectAssessmentId: (state: ProjectState): number => {
    return state.projectAssessmentId;
  },

  projectType: (state: ProjectState): string => {
    return state.project.projectType;
  },

  projectAssessments: (state: ProjectState): ProjectAssessmentDTO[] => {
    return [...(state.project.projectAssessments || [])].sort((pa1, pa2) =>
      moment(pa2.createdAt).diff(moment(pa1.createdAt))
    );
  },

  projectFetching: (state: ProjectState): boolean => {
    return state.projectFetching;
  },

  isProjectEditing: (state: ProjectState): boolean => {
    return state.isProjectEditing;
  },

  projectAssessment: (state: ProjectState, getters: any): ProjectAssessmentDTO => {
    const { projectAssessments } = getters;
    const selectedPA = projectAssessments.find(
      (pa: ProjectAssessmentDTO) => pa.assessmentPeriodId === state.projectAssessmentId
    );
    if (selectedPA) {
      return selectedPA;
    }
    return projectAssessments.length ? projectAssessments[0] : ({} as ProjectAssessmentDTO);
  },

  assessmentStatus: (state: ProjectState, getters: any): string => {
    return getters.projectAssessment.assessmentStatus || PROJECT_ASSESSMENT_STATUSES.PROJECT_CREATION;
  },

  userEngagements: (state: ProjectState, getters: any): UserEngagementDTO[] => {
    return getters.projectAssessment.userEngagements || [];
  },

  peerEvaluations: (state: ProjectState, getters: any): MemberPeerEvaluationDTO[] => {
    return getters.projectAssessment.peerEvaluations || [];
  },

  averageSatAndComplexity: (state: ProjectState): ProjectAssessmentAverageDropsDTO[] => {
    return state.averageSatAndComplexity || [];
  },

  hrmProjects: (state: ProjectState): HRMProjectDTO[] => {
    return state.hrmProjects;
  },

  hrmEditedTimeSheets: (state: ProjectState): HRMUserEngagementTimeSheetDTO[] => {
    return state.hrmEditedTimeSheets;
  },

  managers: (state: ProjectState): ProjectManager[] => {
    return state.managers;
  }
};

const mutations = {
  setProject(state: ProjectState, project: ProjectDTO): void {
    state.project = project;
  },

  setProjectFetching(state: ProjectState, loading: boolean): void {
    state.projectFetching = loading;
  },

  setIsProjectEditing(state: ProjectState, editing: boolean): void {
    state.isProjectEditing = editing;
  },

  setSelectedProjectAssessmentId(state: ProjectState, assessmentPeriodId: number): void {
    state.projectAssessmentId = assessmentPeriodId;
  },

  updateProjectAssessment(state: ProjectState, projectAssessment: ProjectAssessmentDTO): void {
    state.project.projectAssessments = state.project.projectAssessments.map(pa =>
      pa.id === projectAssessment.id ? projectAssessment : pa
    );
    if (projectAssessment.assessmentStatus === PROJECT_ASSESSMENT_STATUSES.RESULT_RELEASE) {
      state.project = { ...state.project, projectStatus: EVALUATION_STATUSES.DONE.label };
    }
  },

  updateUserEngagement(state: ProjectState, payload: any): void {
    const { projectAssessmentId, userEngagement } = payload;
    const projectAssessment = state.project.projectAssessments.find(pa => pa.id === projectAssessmentId);
    if (projectAssessment) {
      const userEngagements = projectAssessment.userEngagements || [];
      projectAssessment.userEngagements = userEngagements.map(ue =>
        ue.id === userEngagement.id ? userEngagement : ue
      );
    }
  },

  setAverageSatAndComplexity(state: ProjectState, payload: ProjectAssessmentAverageDropsDTO[]): void {
    state.averageSatAndComplexity = payload;
  },

  setHRMProjects(state: ProjectState, hrmProjects: HRMProjectDTO[]): void {
    state.hrmProjects = hrmProjects;
  },

  setHRMEditedTimeSheets(state: ProjectState, hrmEditedTimeSheets: HRMUserEngagementTimeSheetDTO[]): void {
    state.hrmEditedTimeSheets = hrmEditedTimeSheets;
  },

  updateHRMTimeSheets(state: ProjectState, hrmTimeSheets: HRMUserEngagementTimeSheetDTO[]): void {
    const { hrmEditedTimeSheets } = state;
    const remappedHRMTimeSheets = remapHrmTimeSheets([...hrmEditedTimeSheets], hrmTimeSheets);
    state.hrmEditedTimeSheets = remappedHRMTimeSheets;
  },

  setManagers(state: ProjectState, managers: ProjectManager[]): void {
    state.managers = managers;
  }
};

const actions = {
  async asyncFetchProjectById({ commit }: any, projectId: string): Promise<{ data: ProjectDTO }> {
    commit('setProjectFetching', true);
    const projectResponse = await fetchProjectById(projectId);
    commit('setProject', projectResponse.data);
    commit('setProjectFetching', false);
    return projectResponse;
  },

  async asyncUpdateProjectInformation({ commit }: any, projectData: ProjectRequestDTO): Promise<void> {
    const projectResponse = await updateProject(projectData);
    commit('setProject', projectResponse.data);
  },

  async asyncUpdateProjectAssessment(
    { commit }: any,
    projectAssessmentPutData: ProjectAssessmentPutDTO
  ): Promise<void> {
    const projectAssessmentResponse = await putProjectAssessment(projectAssessmentPutData);
    commit('updateProjectAssessment', projectAssessmentResponse.data);
  },

  async asyncUpdateProjectTeamMembers({ commit }: any, payload: any): Promise<void> {
    const { members, projectAssessmentId, confirm, key, activityKeyMap } = payload;
    const { data } = await saveUserEngagements(
      { userEngagements: members, activityKeyMap },
      projectAssessmentId,
      confirm,
      key
    );
    commit('updateProjectAssessment', data);
  },

  async asyncUpdateProjectPeerEvaluation({ commit }: any, payload: any): Promise<void> {
    const { members, projectAssessmentId, confirm } = payload;
    const { data } = await saveAdjustReceivedDrops(projectAssessmentId, confirm, undefined, members);
    commit('updateProjectAssessment', data);
  },

  async asyncGetAverageSatAndComplexity({ commit }: any): Promise<void> {
    const { data } = await fetchAverageSatAndComplexity();
    commit('setAverageSatAndComplexity', data);
  },

  async fetchHRMProjects({ commit }: any): Promise<HRMProjectDTO[]> {
    const { data } = await fetchHRMProjects();
    commit('setHRMProjects', data);
    return data;
  },

  async fetchEditedHRMTimeSheets(
    { commit }: any,
    projectAssessmentId: number
  ): Promise<HRMUserEngagementTimeSheetDTO[]> {
    const { data } = await fetchEditedHRMTimeSheetsApi(projectAssessmentId);
    commit('setHRMEditedTimeSheets', data);
    return data;
  },

  async fetchProjectManagers(
    { commit }: any,
    { projectId, assessmentPeriodId }: { projectId: number; assessmentPeriodId: number }
  ): Promise<ProjectManager[]> {
    if (projectId && assessmentPeriodId) {
      const { data } = await fetchProjectManagers(projectId, assessmentPeriodId);
      commit('setManagers', data);
      return data;
    }
    return Promise.resolve([]);
  },

  async updateManagerStatus(
    { commit }: any,
    {
      projectId,
      assessmentPeriodId,
      status,
      managerId
    }: { projectId: number; assessmentPeriodId: number; managerId: number; status: typeof MANAGER_STATUS }
  ): Promise<string> {
    if (projectId && assessmentPeriodId) {
      const { data } = await updateManagerStatus({ projectId, assessmentPeriodId, managerId, status });
      if (data) {
        const { data: managers } = await fetchProjectManagers(projectId, assessmentPeriodId);
        commit('setManagers', managers);
      }
      return data;
    }
    return Promise.resolve('');
  }
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations
};
